import { escapeRegex, ignoresCase, readPattern } from '../GscTools/regex';

/**
 * The topic vocabulary: how a manual query topic is written, what it is sent to Search Console
 * as, and how a returned query is checked against it again in the browser.
 *
 * A topic is a name and one match policy over the query dimension — nothing is discovered,
 * clustered or classified. Search Console has no notion of a topic, so every figure on this report
 * is the answer to the filter a topic compiles to, and the filter is shown wherever its figures
 * are. Topics are independent: each is its own request, a query can meet two topics' rules, and
 * such a query is counted in both.
 */

/**
 * How a topic's terms become filters. Each policy carries its case rule explicitly, because the
 * same words match differently under each and the difference is invisible in the terms alone.
 *
 * - Any: the query contains at least one term, ignoring case. The terms are escaped and compiled
 *   into one `(?i)(a|b)` expression, which is the only faithful way to send an OR of literals.
 * - All: the query contains every term. One `contains` filter per term, ANDed in the request's
 *   one filter group; Search Console's `contains` ignores case.
 * - Exact: the query is exactly one of the terms, case-sensitive like Search Console's `equals`.
 *   One term is sent as `equals`; several as one anchored expression over the escaped terms.
 * - Regex: one RE2 expression, sent as written. It is case-sensitive unless it opens with a flag
 *   group such as (?i), and the report says which of the two it is.
 */
export const POLICY = { ANY: 'any', ALL: 'all', EXACT: 'exact', REGEX: 'regex' };

export const POLICIES = Object.values(POLICY);

/** Product bounds. Every topic costs a totals request per apply, which is what bounds the budget. */
export const MAX_TOPICS = 8;
export const MAX_TERMS = 10;
export const MAX_NAME_LENGTH = 60;
export const MAX_TERM_LENGTH = 200;
export const MAX_PATTERN_LENGTH = 2000;

/** An empty topic editor. `text` holds the terms, semicolon-separated, or the expression. */
export function emptyTopic() {
    return { name: '', policy: POLICY.ANY, text: '' };
}

export function policyLabel(t, policy) {
    return t(`tools.topic_clusters.policies.${policy}`);
}

/**
 * Typed terms, as a list: split on semicolons, trimmed, and with repeats dropped. Under the
 * case-insensitive policies two terms differing only in case are the same term written twice.
 */
export function parseTerms(text, policy) {
    const seen = new Set();
    const terms = [];

    for (const raw of (text ?? '').split(';')) {
        const term = raw.trim();
        if (!term) continue;

        const key = policy === POLICY.EXACT ? term : term.toLowerCase();
        if (seen.has(key)) continue;

        seen.add(key);
        terms.push(term);
    }

    return terms;
}

/** A topic read from what the editor holds: its terms parsed, or its expression trimmed. */
export function topicFromEditor({ name, policy, text }) {
    return policy === POLICY.REGEX
        ? { name: name.trim(), policy, terms: [], pattern: text.trim() }
        : { name: name.trim(), policy, terms: parseTerms(text, policy), pattern: '' };
}

/** The editor's contents for an existing topic, so it can be changed and added back. */
export function editorFromTopic(topic) {
    return {
        name: topic.name,
        policy: topic.policy,
        text: topic.policy === POLICY.REGEX ? topic.pattern : topic.terms.join('; '),
    };
}

const alternatives = (terms) => terms.map(escapeRegex).join('|');

/** Every filter one topic is requested with, in Search Console's own terms. */
export function topicFilters(topic) {
    const filter = (operator, expression) => ({ dimension: 'query', operator, expression });

    switch (topic.policy) {
        case POLICY.ANY:
            return [filter('includingRegex', `(?i)(${alternatives(topic.terms)})`)];
        case POLICY.ALL:
            return topic.terms.map((term) => filter('contains', term));
        case POLICY.EXACT:
            return topic.terms.length === 1
                ? [filter('equals', topic.terms[0])]
                : [filter('includingRegex', `^(${alternatives(topic.terms)})$`)];
        case POLICY.REGEX:
            return [filter('includingRegex', topic.pattern)];
        default:
            return [];
    }
}

/** One filter as Search Console receives it, as text: what block 01 and the exports print. */
export function filterLabel(filter) {
    return `${filter.dimension} ${filter.operator} ${filter.expression}`;
}

/** Whether a topic matches regardless of case — stated, never left to be inferred. */
export function caseInsensitive(topic) {
    if (topic.policy === POLICY.REGEX) return ignoresCase(topic.pattern);
    return topic.policy !== POLICY.EXACT;
}

/** What a topic matches on, as one line: its terms, or its expression. */
export function topicMatch(topic) {
    return topic.policy === POLICY.REGEX ? topic.pattern : topic.terms.join('; ');
}

/**
 * Why one topic's definition cannot be sent, whatever it is called, or null when it can.
 *
 * An empty definition is refused rather than sent: with nothing to match, the request would either
 * be rejected or — worse — come back as every query on the property under a topic's name.
 */
export function definitionError(topic, t) {
    if (!POLICIES.includes(topic?.policy)) return t('tools.topic_clusters.errors.no_policy');

    if (topic.policy === POLICY.REGEX) {
        const pattern = (topic.pattern ?? '').trim();
        if (!pattern) return t('tools.topic_clusters.errors.no_pattern');
        if (pattern.length > MAX_PATTERN_LENGTH) {
            return t('tools.topic_clusters.errors.pattern_long', { max: MAX_PATTERN_LENGTH });
        }

        try {
            readPattern(pattern);
        } catch (err) {
            return t('tools.topic_clusters.errors.invalid_regex', { reason: err.message });
        }

        return null;
    }

    const terms = topic.terms ?? [];
    if (!terms.length) return t('tools.topic_clusters.errors.no_terms');
    if (terms.length > MAX_TERMS) {
        return t('tools.topic_clusters.errors.too_many_terms', { max: MAX_TERMS });
    }
    if (terms.some((term) => term.length > MAX_TERM_LENGTH)) {
        return t('tools.topic_clusters.errors.term_long', { max: MAX_TERM_LENGTH });
    }

    const compiled = topicFilters(topic).reduce((size, item) => size + item.expression.length, 0);
    if (compiled > MAX_PATTERN_LENGTH) {
        return t('tools.topic_clusters.errors.pattern_long', { max: MAX_PATTERN_LENGTH });
    }

    return null;
}

/** Why one topic cannot be sent — its name, then its definition — or null when it can. */
export function topicError(topic, t) {
    const name = (topic?.name ?? '').trim();

    if (!name) return t('tools.topic_clusters.errors.no_name');
    if (name.length > MAX_NAME_LENGTH) {
        return t('tools.topic_clusters.errors.name_long', { max: MAX_NAME_LENGTH });
    }

    return definitionError(topic, t);
}

/**
 * Why one topic cannot be added to the session, or null when it can. A topic whose name is
 * already in use replaces that topic — that is how a topic is edited — so only a new name counts
 * against the bound.
 */
export function addTopicError(topic, topics, t) {
    const error = topicError(topic, t);
    if (error) return error;

    const replacing = topics.some((item) => item.name === topic.name);
    if (!replacing && topics.length >= MAX_TOPICS) {
        return t('tools.topic_clusters.errors.too_many_topics', { max: MAX_TOPICS });
    }

    return null;
}

/**
 * Whether one returned query meets one topic, checked the way Search Console documents its
 * operators: `contains` ignores case, `equals` does not, and an expression is read as above.
 */
function matchesTopic(query, topic) {
    const text = query.toLowerCase();

    switch (topic.policy) {
        case POLICY.ANY:
            return topic.terms.some((term) => text.includes(term.toLowerCase()));
        case POLICY.ALL:
            return topic.terms.every((term) => text.includes(term.toLowerCase()));
        case POLICY.EXACT:
            return topic.terms.includes(query);
        case POLICY.REGEX:
            return readPattern(topic.pattern).test(query);
        default:
            return false;
    }
}

/**
 * The other topics one returned query also meets, by name.
 *
 * This is how an overlap is shown without being acted on: the query stays in every topic it meets
 * and nothing is subtracted or deduplicated. A topic whose expression RegExp cannot read is left
 * out of the check rather than guessed at.
 */
export function otherTopicsFor(query, topics, activeName) {
    const names = [];

    for (const topic of topics) {
        if (topic.name === activeName) continue;

        try {
            if (matchesTopic(query, topic)) names.push(topic.name);
        } catch (err) {
            console.log(err);
        }
    }

    return names;
}
